import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { Map } from 'immutable';
import { get } from 'lodash';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { v4 } from 'uuid';
import { Account } from '../../../account-data/account.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import {
  DepositRequestTrackingStatusesEnum,
  DocumentTypesEnum,
  UPDATE_DEPOSIT_REQUEST_ACTION,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DepositPayment } from '../../../document-data/sub-models/deposit-payment.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { DocumentPreview } from '../../../document-data/sub-models/document-preview.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
// eslint-disable-next-line max-len
import { DIALOG_TYPE as turnOnPaymentsDialog } from '../../../payments/components/turn-on-payments-dialog/turn-on-payments-dialog.component';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { PercentagePipe } from '../../../shared/pipes/percentage/percentage.pipe';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DIALOG_TYPE as depositRequestDialog } from '../deposit-request-dialog/deposit-request-dialog.component';

@Component({
  selector: 'app-add-deposit-request',
  templateUrl: './add-deposit-request.component.html',
  styleUrls: ['./add-deposit-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDepositRequestComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() deposit: Deposit;
  @Input() addDepositForFuture: DepositToggleValue;
  @Input() depositRequestAllAccess: boolean;
  @Input() depositRequestReadAccess: boolean;
  @Input() depositRequestFeatureName: string;
  @Input() enablePaidState: boolean;
  @Input() documentAllAccess: boolean;
  @Input() documentDueDate: string;
  @Input() documentLocale: string;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentCalculationResult: CalculationResult;
  @Input() document: Document;
  @Input() featureToggles: Toggles;
  @Input() account: Account;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output() onSaveDepositRequest: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRemoveDepositRequest: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  onDepositDisabledDialogClose: EventEmitter<IConfirmDialogResult> = new EventEmitter<IConfirmDialogResult>();
  @Output()
  onDepositDialogPreventOpen: EventEmitter<void> = new EventEmitter<void>();
  @Output() onUpdateDepositToggleSettings: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  errorIcon: IIcon;
  depositRequestLabel: string;
  depositRequestTrackingDialogName: TrackingDialogNamesEnum;
  depositRequestTrackingAdditionalProperties: any;
  invalidDeposit: boolean;
  addDepositRequestTrackingAction: ITrackingAction;
  editDepositRequestTrackingAction: ITrackingAction;
  existingDepositDisabledDialogId: string;
  newDepositDisabledDialogId: string;
  DocumentTypesEnum: typeof DocumentTypesEnum;
  depositDisabledTrackingDialogName: TrackingDialogNamesEnum;

  constructor(
    private _percentagePipe: PercentagePipe,
    private _translateService: TranslateService,
    private _calculatorService: CalculatorService,
    private _utilsService: UtilsService,
    private _route: ActivatedRoute,
    private _dialogService: DialogService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.deposit ||
        changes.documentLocale ||
        changes.documentCalculationResult) &&
      this.deposit &&
      this.documentLocale &&
      this.documentCalculationResult
    ) {
      const depositPayment: DepositPayment = this.documentCalculationResult
        .payments.deposit;
      const isNonPercentageScreen = this.deposit.isNonPercentageScreen();
      const hidePercentage: boolean =
        isNonPercentageScreen ||
        get(
          depositPayment,
          DepositPayment.KEYS.PERCENTAGE,
          this.deposit.percentage
        ) > 100;
      const depositRequestLabelKey = hidePercentage
        ? 'LABEL.DEPOSIT_REQUEST'
        : 'LABEL.DEPOSIT_REQUEST_WITH_PERCENTAGE';
      const translateParams = hidePercentage
        ? null
        : {
            percentage: this._percentagePipe.transform(
              this._calculatorService.round(
                get(
                  depositPayment,
                  DepositPayment.KEYS.PERCENTAGE,
                  this.deposit.percentage
                )
              ),
              this.documentLocale,
              0
            ),
          };

      this.depositRequestLabel = this._translateService.instant(
        depositRequestLabelKey,
        translateParams
      );
    }

    if (
      (changes.deposit || changes.documentCalculationResult) &&
      this.documentCalculationResult
    ) {
      const depositPayment: DepositPayment = this.documentCalculationResult
        .payments.deposit;
      let depositRequestStatus: string = null;

      if (!this.deposit) {
        depositRequestStatus = DepositRequestTrackingStatusesEnum.no_request;
      } else if (this.documentCalculationResult && depositPayment) {
        if (depositPayment.amountRemaining) {
          depositRequestStatus = depositPayment.amountPaid
            ? DepositRequestTrackingStatusesEnum.partially_paid
            : DepositRequestTrackingStatusesEnum.unpaid;
        }
      }

      this.depositRequestTrackingAdditionalProperties = {
        deposit_request_status: depositRequestStatus,
      };

      if (
        this.deposit &&
        this.documentCalculationResult &&
        this.documentCalculationResult.payments
      ) {
        const depositAmountPaid = get(
          depositPayment,
          DepositPayment.KEYS.AMOUNT_PAID,
          0
        );

        this.invalidDeposit =
          depositPayment &&
          depositPayment.totalAmount >
            this.documentCalculationResult.outstandingBalance +
              depositAmountPaid &&
          depositPayment.isUnpaid();
      }
    }

    if (changes.trackingScreenName && this.trackingScreenName) {
      this.addDepositRequestTrackingAction = this._trackingActionBuilder.buildButtonTapped(
        'add_deposit_request',
        this.trackingScreenName
      );
      this.editDepositRequestTrackingAction = this._trackingActionBuilder.buildButtonTapped(
        'deposit_request',
        this.trackingScreenName
      );
    }

    if (changes.documentType && this.documentType) {
      switch (this.documentType) {
        case DocumentTypesEnum.invoice:
          this.depositRequestTrackingDialogName =
            TrackingDialogNamesEnum.invoice_deposit_request;
          break;
        case DocumentTypesEnum.estimate:
          this.depositRequestTrackingDialogName =
            TrackingDialogNamesEnum.estimate_deposit_request;
          break;
        default:
          this.depositRequestTrackingDialogName = null;
          break;
      }
    }
  }

  ngOnInit() {
    this.errorIcon = {
      name: 'error',
      size: IconSizesEnum.small,
    };
    this.existingDepositDisabledDialogId = v4();
    this.newDepositDisabledDialogId = v4();
    this.depositDisabledTrackingDialogName =
      TrackingDialogNamesEnum.deposit_estimate_disabled;
    this.DocumentTypesEnum = DocumentTypesEnum;
  }

  ngAfterViewInit() {
    this._route.queryParamMap
      .pipe(
        takeUntil(this._destroyed),
        map((queryParamMap) => queryParamMap.get('action')),
        filter((action: string) => action === UPDATE_DEPOSIT_REQUEST_ACTION)
      )
      .subscribe(() => setTimeout(() => this.openDepositRequestDialog()));
  }

  openDepositRequestDialog() {
    if (
      this.depositRequestAllAccess ||
      (!this.depositRequestAllAccess && this.deposit)
    ) {
      const convertedDoc: DocumentPreview = this._utilsService.getConvertedDoc(
        this.document
      );

      if (this.documentType === DocumentTypesEnum.estimate && convertedDoc) {
        this._dialogService.openConfirmDialog(
          this.deposit
            ? this.existingDepositDisabledDialogId
            : this.newDepositDisabledDialogId
        );
      } else {
        this._dialogService.openDialog({ type: depositRequestDialog });
      }
    } else {
      if (this.depositRequestReadAccess) {
        this._dialogService.openGrowthDialog(
          BannerCanvasTypesEnum.feature_lock,
          this.depositRequestFeatureName
        );
      }

      this.onDepositDialogPreventOpen.emit();
    }
  }

  saveDepositRequest(deposit: Deposit) {
    if (this.depositRequestAllAccess && this.documentAllAccess) {
      if (
        (this.paymentsSettings.cardPaymentsEnabled() &&
          !this.documentSettings.cardPaymentsDisabled) ||
        (this.paymentsSettings.paypalEcEnabled &&
          !this.documentSettings.paypalEcDisabled)
      ) {
        this.onSaveDepositRequest.emit(deposit);
      } else {
        this._dialogService.openDialog({ type: turnOnPaymentsDialog });
      }
    }
  }

  removeDepositRequest() {
    if (this.depositRequestAllAccess && this.documentAllAccess) {
      this.onRemoveDepositRequest.emit();
    }
  }

  updateDepositToggleSettings(depositToggleProps: Map<string, any>) {
    if (this.depositRequestAllAccess && this.documentAllAccess) {
      this.onUpdateDepositToggleSettings.emit(depositToggleProps);
    }
  }

  ngOnDestroy() {
    this._dialogService.closeConfirmDialog(
      this.existingDepositDisabledDialogId
    );
    this._dialogService.closeConfirmDialog(this.newDepositDisabledDialogId);
    this._dialogService.closeDialog({ type: depositRequestDialog });
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      this.depositRequestFeatureName
    );
  }
}
